import type { FC, ReactNode } from 'react'
import type { Stat } from '@/types'

import Image from 'next/image'
import { pad } from '@/lib/format'
import { Mark } from '@/components/generals/mark'
import { PlaceholderTag } from '@/components/generals/placeholder-tag'
import { SectionLabel } from '@/components/generals/section-label'

interface ImpactSectionProps {
    stats: Stat[]
}

const IMAGE = 'https://blitz-template.cdn.modulify.website/images/projects/nexa-2.jpg'

const ImpactSection: FC<ImpactSectionProps> = ({ stats }): ReactNode => {
    const hasPlaceholders = stats.some((stat) => stat.isPlaceholder)

    return (
        <section id='impact' aria-labelledby='impact-title' data-nav-theme='light' className='relative overflow-hidden bg-stone text-charcoal'>
            <div className='mx-auto grid max-w-[1440px] gap-16 px-5 py-20 md:px-10 md:py-28 lg:grid-cols-12 lg:gap-10'>
                <div className='lg:col-span-5'>
                    <div data-reveal>
                        <SectionLabel>Marketing impact</SectionLabel>
                        <h2 id='impact-title' className='mt-6 font-heading type-section'>
                            <span className='block'>Creative thinking.</span>
                            <span className='block'><Mark>Real business impact.</Mark></span>
                        </h2>
                        <p className='mt-8 max-w-md text-[17px] leading-relaxed text-charcoal/85'>
                            We combine strategic direction, creative execution, and performance measurement to help brands move forward.
                        </p>
                    </div>

                    <figure className='relative mt-12 max-w-[22rem]'>
                        <div aria-hidden='true' className='absolute -bottom-4 -right-4 h-2/3 w-2/3 bg-coral' />
                        <div data-reveal-image className='relative aspect-[4/5] overflow-hidden bg-cream'>
                            <Image src={IMAGE} alt='Person standing on a geometric concrete structure against a cloudy sky' fill sizes='352px' className='object-cover' />
                        </div>
                        <figcaption className='relative mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-charcoal/80'>
                            NEXA / Concept campaign
                        </figcaption>
                    </figure>
                </div>

                <div className='lg:col-span-7'>
                    <ul className='grid grid-cols-1 border-t border-charcoal sm:grid-cols-2'>
                        {stats.map((stat, index) => (
                            <li
                                key={stat.id}
                                data-reveal
                                className='flex flex-col justify-between gap-10 border-b border-charcoal/30 py-8 sm:min-h-[16rem] sm:px-6 sm:py-10 sm:[&:nth-child(odd)]:border-r sm:[&:nth-child(odd)]:pl-0'
                            >
                                <span aria-hidden='true' className='font-mono text-[11px] text-charcoal/75'>/ {pad(index + 1)}</span>
                                <div>
                                    <p className='font-heading font-wide text-[clamp(3rem,6vw,5.75rem)] font-black leading-[0.85] tracking-[-0.05em]'>
                                        <span aria-hidden='true' data-count>{stat.value}</span>
                                        <span className='sr-only'>{stat.value}</span>
                                    </p>
                                    <span aria-hidden='true' className='mt-5 block h-1 w-12 bg-coral' />
                                    <p className='mt-4 text-[15px] font-medium text-charcoal/85'>{stat.label}</p>
                                </div>
                            </li>
                        ))}
                    </ul>

                    {hasPlaceholders && (
                        <p className='mt-6 flex flex-wrap items-center gap-3 text-[13px] text-charcoal/80'>
                            <PlaceholderTag>Illustrative figures</PlaceholderTag>
                            Placeholder numbers, to be replaced with verified agency results.
                        </p>
                    )}
                </div>
            </div>
        </section>
    )
}

export default ImpactSection
